import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { API } from '../config/api';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import { 
  ArrowLeft, Loader2, Check, Clock, MapPin, DollarSign,
  Paintbrush, Hammer, Trees, Zap, Layers, Building, Wrench, Sparkles, Home as HomeIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { format, addDays, isBefore, startOfDay } from 'date-fns';

const serviceIcons = {
  decorating: Paintbrush,
  carpentry: Hammer,
  outdoors: Trees,
  electrics: Zap,
  plastering: Layers,
  building: Building,
  general: Wrench,
  cleaning: Sparkles,
  landlord: HomeIcon
};

export const Booking = () => {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [notes, setNotes] = useState('');
  const [success, setSuccess] = useState(false);
  const [bookingDetails, setBookingDetails] = useState(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { state: { from: { pathname: `/booking/${quoteId}` } } });
    }
  }, [isAuthenticated, authLoading, navigate, quoteId]);

  useEffect(() => {
    // Scroll to top when page loads
    window.scrollTo(0, 0);
  }, []);

  const fetchQuote = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/quotes/${quoteId}`);
      setQuote(response.data);
      if (response.data.status === 'booked') {
        toast.error('This quote has already been booked');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error('Quote not found');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [quoteId, navigate]);

  const fetchAvailability = useCallback(async (date) => {
    try {
      const response = await axios.get(`${API}/availability/${date}`);
      setAvailableSlots(response.data.available_slots);
      setBookedSlots(response.data.booked_slots);
      setSelectedTime('');
    } catch (error) {
      console.error('Failed to fetch availability:', error);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchQuote();
    }
  }, [isAuthenticated, quoteId, fetchQuote]);

  useEffect(() => {
    if (selectedDate) {
      fetchAvailability(format(selectedDate, 'yyyy-MM-dd'));
    }
  }, [selectedDate, fetchAvailability]);

  const handleSubmit = async () => {
    if (!selectedDate || !selectedTime) {
      toast.error('Please select a date and time');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/bookings`, {
        quote_id: quoteId,
        booking_date: format(selectedDate, 'yyyy-MM-dd'),
        booking_time: selectedTime,
        notes: notes
      });
      setBookingDetails(response.data);
      setSuccess(true);
      toast.success('Booking confirmed!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setSubmitting(false);
    }
  };

  const disabledDays = (date) => {
    return isBefore(date, startOfDay(new Date()));
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (success && bookingDetails) {
    return (
      <div className="min-h-[calc(100vh-4rem)] bg-muted/30 py-8 md:py-12">
        <div className="max-w-2xl mx-auto px-4 animate-in">
          <Card className="border-border shadow-xl overflow-hidden">
            <div className="bg-green-500 text-white p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8" />
              </div>
              <h1 className="text-2xl md:text-3xl font-bold font-['Outfit']">Booking Confirmed!</h1>
              <p className="text-white/80 mt-2">We'll see you soon</p>
            </div>
            
            <CardContent className="p-6 md:p-8">
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-muted rounded-xl">
                  <Clock className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Date & Time</p>
                    <p className="font-semibold">
                      {format(new Date(bookingDetails.booking_date), 'EEEE, MMMM d, yyyy')} at {bookingDetails.booking_time}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-4 bg-muted rounded-xl">
                  <MapPin className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Service Address</p>
                    <p className="font-semibold">{bookingDetails.address}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-4 bg-muted rounded-xl">
                  <DollarSign className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Estimated Price</p>
                    <p className="font-semibold text-lg">£{(bookingDetails.price_min || bookingDetails.total_cost || 0).toFixed(0)}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-4 bg-green-50 border border-green-200 rounded-xl">
                  <Check className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="text-sm text-green-600">Deposit (25%)</p>
                    <p className="font-semibold text-lg text-green-700">£{(((bookingDetails.price_min || bookingDetails.total_cost || 0) * 0.25)).toFixed(0)}</p>
                    <p className="text-xs text-green-600">Balance due on completion</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 mt-8">
                <Button 
                  className="flex-1 h-12 rounded-full"
                  onClick={() => navigate('/dashboard')}
                  data-testid="go-to-dashboard-btn"
                >
                  Go to Dashboard
                </Button>
                <Button 
                  variant="outline"
                  className="flex-1 h-12 rounded-full"
                  onClick={() => navigate('/')}
                  data-testid="back-to-home-btn"
                >
                  Back to Home
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const ServiceIcon = quote ? serviceIcons[quote.service_id] || Wrench : Wrench;

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-muted/30 py-8 md:py-12">
      <div className="max-w-4xl mx-auto px-4">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)} 
          className="mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>

        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold font-['Outfit'] tracking-tight">
            Schedule Your Service
          </h1>
          <p className="text-muted-foreground mt-2">Choose a date and time that works for you</p>
        </div>

        {quote && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Calendar & Time Selection */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="border-border">
                <CardHeader>
                  <CardTitle className="font-['Outfit']">Select Date</CardTitle>
                </CardHeader>
                <CardContent>
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={disabledDays}
                    className="rounded-xl border-0"
                    data-testid="booking-calendar"
                  />
                </CardContent>
              </Card>

              {selectedDate && (
                <Card className="border-border animate-in">
                  <CardHeader>
                    <CardTitle className="font-['Outfit']">
                      Available Times for {format(selectedDate, 'MMM d')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {availableSlots.length === 0 ? (
                      <p className="text-muted-foreground text-center py-4">
                        No available slots for this date. Please select another day.
                      </p>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                        {availableSlots.map((slot) => (
                          <button
                            key={slot}
                            onClick={() => setSelectedTime(slot)}
                            className={`time-slot ${selectedTime === slot ? 'selected' : ''}`}
                            data-testid={`time-slot-${slot}`}
                          >
                            {slot}
                          </button>
                        ))}
                      </div>
                    )}
                    
                    {bookedSlots.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-border">
                        <p className="text-sm text-muted-foreground mb-2">Unavailable times:</p>
                        <div className="flex flex-wrap gap-2">
                          {bookedSlots.map((slot) => (
                            <span key={slot} className="time-slot booked text-xs">
                              {slot}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              <Card className="border-border">
                <CardHeader>
                  <CardTitle className="font-['Outfit']">Additional Notes (Optional)</CardTitle>
                </CardHeader>
                <CardContent>
                  <Textarea
                    placeholder="Any special instructions or access details..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="min-h-24"
                    data-testid="booking-notes-input"
                  />
                </CardContent>
              </Card>
            </div>

            {/* Quote Summary */}
            <div className="lg:col-span-1">
              <Card className="border-border sticky top-24">
                <CardHeader className="bg-muted/50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <ServiceIcon className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-lg font-['Outfit']">{quote.service_name}</CardTitle>
                      <p className="text-sm text-muted-foreground">{quote.job_size} job</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
                    <p className="text-sm">{quote.address}</p>
                  </div>
                  
                  <div className="border-t border-border pt-4 space-y-3">
                    {/* Show lower price estimate */}
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Estimated Price</span>
                      <span className="font-semibold text-lg">£{(quote.price_min || quote.total_cost || 0).toFixed(0)}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Based on the job description provided. Final price confirmed on completion.
                    </p>
                    
                    {/* Deposit section */}
                    <div className="bg-green-50 border border-green-200 rounded-xl p-4 mt-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-semibold text-green-800">Deposit to Book</p>
                          <p className="text-xs text-green-600">25% secures your slot</p>
                        </div>
                        <span className="text-2xl font-bold text-green-600">
                          £{(((quote.price_min || quote.total_cost || 0) * 0.25)).toFixed(0)}
                        </span>
                      </div>
                    </div>
                    
                    <div className="bg-muted/50 rounded-lg p-3 text-xs text-muted-foreground">
                      <p className="font-medium text-foreground mb-1">How it works:</p>
                      <ul className="space-y-1">
                        <li>• Pay 25% deposit now to secure your booking</li>
                        <li>• Remaining balance due on job completion</li>
                        <li>• Final price may adjust based on actual work</li>
                      </ul>
                    </div>
                  </div>

                  {selectedDate && selectedTime && (
                    <div className="p-3 bg-secondary rounded-lg text-center">
                      <p className="text-sm text-muted-foreground">Selected</p>
                      <p className="font-semibold">
                        {format(selectedDate, 'MMM d')} at {selectedTime}
                      </p>
                    </div>
                  )}

                  <Button
                    className="w-full btn-accent h-12"
                    onClick={handleSubmit}
                    disabled={!selectedDate || !selectedTime || submitting}
                    data-testid="confirm-booking-btn"
                  >
                    {submitting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      `Pay £${(((quote.price_min || quote.total_cost || 0) * 0.25)).toFixed(0)} Deposit`
                    )}
                  </Button>
                  
                  <p className="text-xs text-center text-muted-foreground">
                    No payment taken today – we'll contact you to confirm
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
